<template>
  <div class="contact-section">
    
    <!-- Back to top button with animation -->
    <div 
      class="back-to-top" 
      @click="scrollToTop"
      @mouseenter="isHovering = true" 
      @mouseleave="isHovering = false"
    >
      <div class="btn-content">
        <font-awesome-icon 
          :icon="['fas', 'circle-chevron-up']" 
          class="icon" 
          :class="{ 'animated': isHovering }" 
        />
        <span>Back to Top</span>
      </div>
    </div>
    
    <!-- Main content with glassmorphism effect -->
    <div class="contact-container">
      <div class="contact-wrapper" ref="contactWrapper">
        <div class="contact-info" :class="{ 'in-view': isInView }">
          <!-- Address column -->
          <div 
            class="column address-column" 
            @mousemove="handleParallax" 
            ref="addressColumn"
          >
            <div class="column-content">
              <div class="column-header">
                <div class="accent-line accent-line-left"></div>
                <h3>Address</h3>
                <div class="accent-line accent-line-right"></div>
              </div>
              <div class="column-body text-left">
                <p>3022 E. County Rd S</p>
                <p>Beloit, WI 53511</p>
                <div class="map-link">
                  <a href="#" @click.prevent="openMapModal">
                    <font-awesome-icon :icon="['fas', 'location-dot']" /> View on Map
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Contact column -->
          <div 
            class="column contact-column" 
            @mousemove="handleParallax" 
            ref="contactColumn"
          >
            <div class="column-content">
              <div class="column-header">
                <div class="accent-line accent-line-left"></div>
                <h3>Contact</h3>
                <div class="accent-line accent-line-right"></div>
              </div>
              <div class="column-body">
                <div class="contact-item flex justify-between">
                  <span class="contact-label text-secondary/50">Customer Service:</span>
                  <div class="tooltip-container">
                    <a href="tel:8557838721" class="icon-link" aria-label="Sales Phone Number">
                      <font-awesome-icon :icon="['fas', 'phone']" />
                      <span class="tooltip-text">(855) STE-USA1 ext. 2</span>
                    </a>
                    <a href="mailto:sales@ste-usa.com" class="icon-link ml-2" aria-label="Sales Email">
                      <font-awesome-icon :icon="['fas', 'envelope']" />
                      <span class="tooltip-text">sales@ste-usa.com</span>
                    </a>
                  </div>
                </div>
                <div class="contact-item flex justify-between">
                  <span class="contact-label text-secondary/50">Parts and Service:</span>
                  <div class="tooltip-container">
                    <a href="tel:6083689663,7" class="icon-link" aria-label="Service Phone Number">
                      <font-awesome-icon :icon="['fas', 'phone']" />
                      <span class="tooltip-text">(855) STE-USA1 ext. 3</span>
                    </a>
                    <a href="mailto:service@ste-usa.com" class="icon-link ml-2" aria-label="Service Email">
                      <font-awesome-icon :icon="['fas', 'envelope']" />
                      <span class="tooltip-text">service@ste-usa.com</span>
                    </a>
                  </div>
                </div>
                <div class="contact-item flex justify-between">
                  <span class="contact-label text-secondary/50">Human Resources:</span>
                  <div class="tooltip-container">
                    <a href="tel:6083138367,4" class="icon-link" aria-label="HR Phone Number">
                      <font-awesome-icon :icon="['fas', 'phone']" />
                      <span class="tooltip-text">(855) STE-USA1 ext. 5</span>
                    </a>
                    <a href="mailto:hr@ste-usa.com" class="icon-link ml-2" aria-label="HR Email">
                      <font-awesome-icon :icon="['fas', 'envelope']" />
                      <span class="tooltip-text">hr@ste-usa.com</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Resources column -->
          <div 
            class="column resources-column" 
            @mousemove="handleParallax" 
            ref="resourcesColumn"
          >
            <div class="column-content">
              <div class="column-header">
                <div class="accent-line accent-line-left"></div>
                <h3>Resources</h3>
                <div class="accent-line accent-line-right"></div>
              </div>
              <div class="column-body text-left">
                <div class="flex flex-col gap-1">
                  <a href="https://news.ste-usa.com/terms-of-sale" target="_blank" rel="noopener" class="resource-link">Terms of Sale</a>
                  <a href="https://news.ste-usa.com/terms-of-purchase" target="_blank" rel="noopener" class="resource-link">Terms of Purchase</a>
                  <a href="https://news.ste-usa.com/privacy-policy" target="_blank" rel="noopener" class="resource-link">Privacy Policy</a>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Connect column -->
          <div 
            class="column connect-column" 
            @mousemove="handleParallax" 
            ref="connectColumn"
          >
            <div class="column-content">
              <div class="column-header">
                <div class="accent-line accent-line-left"></div>
                <h3>Connect</h3>
                <div class="accent-line accent-line-right"></div>
              </div>
              <div class="column-body">
                <div class="social-links">
                  <a href="https://www.facebook.com/stainlesstankandequipment/" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="Facebook">
                    <font-awesome-icon :icon="['fab', 'facebook']" />
                  </a>
                  <a href="https://www.linkedin.com/company/stainlesstanker/posts/?feedView=all" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="LinkedIn">
                    <font-awesome-icon :icon="['fab', 'linkedin']" />
                  </a>
                  <a href="https://www.instagram.com/company/stainlesstanker/?hl=am-et" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="Instagram">
                    <font-awesome-icon :icon="['fab', 'instagram']" />
                  </a>
                </div>
                <div class="newsletter">
                  <p>Subscribe to our newsletter</p>
                  <div class="newsletter-form">
                    <input type="email" placeholder="Enter your email" class="email-input" />
                    <button class="subscribe-btn">
                      <font-awesome-icon :icon="['fas', 'arrow-right']" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Copyright info -->
        <div class="copyright">
          <p>© {{ currentYear }} Stainless Tank & Equipment - All rights reserved.</p>
          <p class="tagline">American Made Stainless Steel Tank Trailers</p>
        </div>
      </div>
    </div>

    <!-- Enhanced Map Modal -->
    <transition name="modal-fade">
      <div 
        class="map-modal-overlay" 
        v-if="isMapModalOpen" 
        @click.self="closeMapModal"
        :class="{ 'modal-active': isMapModalActive }"
      >
        <!-- Decorative background elements -->
        <div class="modal-bg-elements">
          <div class="geo-shape geo-dots"></div>
        </div>
        
        <div 
          class="map-modal-container" 
          :class="{ 'in-view': isMapModalActive }"
          ref="mapModal"
          @mousemove="handleModalParallax"
        >
          <!-- Decorative corner accents -->
          <div class="corner-accent top-left"></div>
          <div class="corner-accent top-right"></div>
          <div class="corner-accent bottom-left"></div>
          <div class="corner-accent bottom-right"></div>
          
          <div class="map-modal-header pt-4 px-4">
            <div class="map-modal-title bg-gradient-to-l from-base-content/0 to-base-300/100 p-2 w-1/2 rounded-2xl">
              <div class="icon-container">
                <font-awesome-icon :icon="['fas', 'location-dot']" class="location-icon" />
                <div class="pulse-ring"></div>
              </div>
              <h3>Our Location</h3>
            </div>
            <button 
              class="modal-close" 
              @click="closeMapModal" 
              aria-label="Close modal"
              @mouseenter="isCloseHovered = true"
              @mouseleave="isCloseHovered = false"
            >
              <font-awesome-icon :icon="['fas', 'rectangle-xmark']" :class="{ 'hover': isCloseHovered }" />
            </button>
          </div>
          
          <div class="map-modal-content">
            <div class="map-container">
              <div class="map-overlay"></div>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2952.0191961246733!2d-89.0234296!3d42.505497!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8808760e2e6cd80f%3A0x7e3114bd9a044622!2s3022%20E%20County%20Rd%20S%2C%20Beloit%2C%20WI%2053511%2C%20USA!5e0!3m2!1sen!2s!4v1720372418825!5m2!1sen!2s" 
                width="100%" 
                height="450" 
                style="border:0;" 
                allowfullscreen="" 
                loading="lazy" 
                referrerpolicy="no-referrer-when-downgrade"
                title="STE location on Google Maps"
                @load="mapLoaded = true"
              ></iframe>
              <div class="loading-indicator" v-if="!mapLoaded">
                <font-awesome-icon :icon="['fas', 'spinner']" class="spinner" />
                <p>Loading map...</p>
              </div>
            </div>
            
            <div class="map-info">
              <div 
                class="info-card"
                :class="{ 'info-loaded': mapLoaded }"
              >
                <div class="card-bg-effect"></div>
                <div class="info-content">
                  <h4>STE Tank and Equipment</h4>
                  <div class="info-details">
                    <p>3022 E. County Rd S</p>
                    <p>Beloit, WI 53511</p>
                  </div>
                  <div class="directions-link">
                    <a 
                      href="https://maps.app.goo.gl/dyz8orBx7PQSQVdg8" 
                      target="_blank" 
                      rel="noopener"
                      class="cta-button"
                      @mouseenter="isDirectionsHovered = true"
                      @mouseleave="isDirectionsHovered = false"
                    >
                      <span class="cta-text">Get Directions</span>
                      <span class="cta-icon">
                        <font-awesome-icon 
                          :icon="['fas', isDirectionsHovered ? 'arrow-right' : 'right-long']" 
                        />
                      </span>
                      <span class="cta-background"></span>
                    </a>
                  </div>
                </div>
              </div>
              
              <div 
                class="info-highlight"
                :class="{ 'info-loaded': mapLoaded }"
              >
                <div class="highlight-content">
                  <font-awesome-icon :icon="['fas', 'phone']" class="highlight-icon" />
                  <div>
                    <p class="highlight-label">Need assistance finding us?</p>
                    <p class="highlight-value"><a href="tel:8557838721">(855) STE-USA1</a></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'ContactSection',
  data() {
    return {
      isHovering: false,
      isInView: false,
      isMapModalOpen: false,
      isMapModalActive: false,
      isCloseHovered: false,
      isDirectionsHovered: false,
      mapLoaded: false,
      currentYear: new Date().getFullYear(),
      observer: null,
      parallaxElements: {
        addressColumn: { x: 0, y: 0 },
        contactColumn: { x: 0, y: 0 },
        resourcesColumn: { x: 0, y: 0 },
        connectColumn: { x: 0, y: 0 }
      },
      modalMousePosition: { x: 0, y: 0 }
    }
  },
  mounted() {
    this.setupIntersectionObserver();
    
    // Initialize parallax references after DOM is ready
    this.$nextTick(() => {
      this.parallaxElements = {
        addressColumn: this.$refs.addressColumn,
        contactColumn: this.$refs.contactColumn,
        resourcesColumn: this.$refs.resourcesColumn,
        connectColumn: this.$refs.connectColumn
      };
    });

    // Add event listener for escape key to close modal
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    scrollToTop() {
      const container = document.querySelector('.fullpage-container') || 
                        document.querySelector('.overflow-y-auto');
      
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    
    setupIntersectionObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.isInView = true;
          } else {
            this.isInView = false;
          }
        },
        { threshold: 0.1 }
      );
      
      if (this.$refs.contactWrapper) {
        this.observer.observe(this.$refs.contactWrapper);
      }
    },
    
    handleParallax(e) {
      if (!this.$refs.contactWrapper) return;
      
      const element = e.currentTarget;
      const rect = element.getBoundingClientRect();
      
      // Calculate position relative to element center
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      
      // Normalize values between -1 and 1
      const normalizedX = x / (rect.width / 2);
      const normalizedY = y / (rect.height / 2);
      
      // Apply subtle transform to create parallax effect
      element.style.transform = `
        translate3d(0, 0, 0) 
        rotateX(${normalizedY * -2}deg) 
        rotateY(${normalizedX * 2}deg)
      `;
      
      // Reset transform on mouse leave
      element.addEventListener('mouseleave', () => {
        element.style.transform = 'translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg)';
      }, { once: true });
    },
    
    handleModalParallax(e) {
      if (!this.$refs.mapModal) return;
      
      const modal = this.$refs.mapModal;
      const rect = modal.getBoundingClientRect();
      
      // Calculate position relative to element center
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      
      // Store mouse position for animations
      this.modalMousePosition = { x, y };
      
      // Apply subtle transform for parallax effect on modal
      modal.style.transform = `
        perspective(1000px) 
        rotateX(${y * -1}deg) 
        rotateY(${x * 1}deg)
        scale3d(1, 1, 1)
      `;
      
      // Update corner accents based on mouse position
      const corners = modal.querySelectorAll('.corner-accent');
      corners.forEach(corner => {
        const isLeft = corner.classList.contains('top-left') || corner.classList.contains('bottom-left');
        const isTop = corner.classList.contains('top-left') || corner.classList.contains('top-right');
        
        const offsetX = isLeft ? -x * 15 : x * 15;
        const offsetY = isTop ? -y * 15 : y * 15;
        
        corner.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
      });
      
      // Reset transforms when mouse leaves
      modal.addEventListener('mouseleave', () => {
        modal.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
        corners.forEach(corner => {
          corner.style.transform = 'translate(0, 0)';
        });
      }, { once: true });
    },
    
    openMapModal() {
      this.isMapModalOpen = true;
      document.body.classList.add('modal-open');
      
      // Set a small timeout to trigger the entrance animation after the modal is in the DOM
      setTimeout(() => {
        this.isMapModalActive = true;
      }, 50);
    },
    
    closeMapModal() {
      this.isMapModalActive = false;
      this.mapLoaded = false;
      
      // Add a delay to match the exit animation
      setTimeout(() => {
        this.isMapModalOpen = false;
        document.body.classList.remove('modal-open');
      }, 300);
    },
    
    handleKeyDown(e) {
      if (e.key === 'Escape' && this.isMapModalOpen) {
        this.closeMapModal();
      }
    }
  }
}
</script>

<style scoped>
.contact-section {
  width: 100%;
  position: relative;
  background: linear-gradient(135deg, #1a1a1a, #2c2c2c);
  padding: 0;
  margin-top: 4rem;
  overflow: hidden;
}

/* Back to top button */
.back-to-top {
  position: relative;
  width: 100%;
  padding: 15px 0;
  background: linear-gradient(90deg, rgba(143, 0, 0, 0.8) 0%, rgba(183, 0, 0, 0.8) 100%);
  color: white;
  cursor: pointer;
  z-index: 10;
  overflow: hidden;
  transition: all 0.3s ease;
}

.back-to-top::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, rgba(183, 0, 0, 0.8) 0%, rgba(143, 0, 0, 0.8) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.back-to-top:hover::before {
  opacity: 1;
}

.btn-content {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  z-index: 2;
}

.icon {
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.icon.animated {
  animation: bounce 1s ease infinite;
}

/* Main contact container */
.contact-container {
  position: relative;
  z-index: 2;
  width: 100%;
  background: radial-gradient(circle at center, #363741, #22232c);
}

.contact-wrapper {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 1rem;
  transition: opacity 0.5s ease, transform 0.5s ease;
}

/* Contact info layout */
.contact-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}

.contact-info.in-view {
  opacity: 1;
  transform: translateY(0);
}

/* Column styling */
.column {
  position: relative;
  perspective: 1000px;
  transition: transform 0.3s ease-out;
  will-change: transform;
}

.column-content {
  background: rgba(255, 255, 255, 0.03);
  border-radius: var(--radius-box);
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  height: 100%;
  transition: all 0.3s ease;
}

.column:hover .column-content {
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.column-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.2rem;
  position: relative;
}

.accent-line {
  height: 3px;
  background: var(--color-primary);
  transition: width 0.3s ease;
}

.accent-line-left {
  width: 40px;
  margin-right: 10px;
}

.accent-line-right {
  width: 0; /* Hidden on desktop by default */
  margin-left: 10px;
  opacity: 0;
}

.column:hover .accent-line-left {
  width: 60px;
}

/* Mobile styles */
@media (max-width: 768px) {
  .column-header {
    justify-content: center;
  }
  
  .accent-line-left, .accent-line-right {
    width: 55px; /* Equal width on both sides */
    margin: 0 15px; /* Space between lines and text */
    opacity: 1;
  }
  
  .column:hover .accent-line-left,
  .column:hover .accent-line-right {
    width: 80px; /* Grow from both sides on hover */
  }
  
  h3 {
    text-align: center;
  }
}

/* Address column specific */
.address-column .map-link {
  margin-top: 1rem;
  padding-top: 0.5rem;
  border-top: 1px dashed rgba(255, 255, 255, 0.1);
}

.address-column .map-link a {
  color: var(--color-secondary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.3s ease;
}

.address-column .map-link a:hover {
  color: var(--color-primary);
}

/* Contact column specific */
.contact-item {
  margin-bottom: 0.25rem;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1rem;
}

.contact-label {
  font-size: 0.9rem;
  opacity: 0.7;
  margin-bottom: 0.2rem;
}

.contact-link {
  color: var(--color-secondary);
  transition: color 0.3s ease;
}

.contact-link:hover {
  color: var(--color-primary);
  font-weight: bold;
}

/* Tooltip styles */
.tooltip-container {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.icon-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-secondary);
  background: rgba(255, 255, 255, 0.1);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: all 0.3s ease;
  position: relative;
}

.icon-link:hover {
  background: var(--color-primary);
  color: white;
  transform: translateY(-2px);
}

.tooltip-text {
  visibility: hidden;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(29, 30, 38, 0.9);
  color: white;
  text-align: center;
  padding: 5px 10px;
  border-radius: 6px;
  white-space: nowrap;
  font-size: 0.8rem;
  opacity: 0;
  transition: opacity 0.3s, visibility 0.3s, transform 0.3s;
  z-index: 100;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  pointer-events: none;
  transform: translateX(-50%) translateY(5px);
  margin-bottom: 5px;
}

.tooltip-text::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: rgba(29, 30, 38, 0.9) transparent transparent transparent;
}

.icon-link:hover .tooltip-text,
.icon-link:focus .tooltip-text {
  visibility: visible;
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.ml-2 {
  margin-left: 0.5rem;
}

/* Resources column specific */

.resource-link {
  color: var(--color-secondary);
  padding: 0.3rem 0;
  position: relative;
  transition: color 0.3s ease;
}

.resource-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 1px;
  background-color: var(--color-primary);
  transition: width 0.3s ease;
}

.resource-link:hover {
  color: var(--color-primary);
  font-weight: bold;
}

.resource-link:hover::after {
  width: 100%;
}

/* Connect column specific */
.social-links {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.social-link {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-secondary);
  transition: all 0.3s ease;
}

.social-link:hover {
  background: var(--color-primary);
  color: white;
  transform: translateY(-3px);
}

.newsletter p {
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.newsletter-form {
  display: flex;
  overflow: hidden;
  border-radius: var(--radius-field);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.email-input {
  flex-grow: 1;
  background: rgba(0, 0, 0, 0.2);
  border: none;
  padding: 0.75rem 1rem;
  color: white;
  outline: none;
}

.subscribe-btn {
  background: var(--color-primary);
  color: white;
  border: none;
  padding: 0 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.subscribe-btn:hover {
  background-color: #a70000;
}

/* Copyright section */
.copyright {
  text-align: center;
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
}

.tagline {
  margin-top: 0.5rem;
  font-style: italic;
  font-size: 0.85rem;
  color: var(--color-secondary);
  opacity: 0.7;
}

/* Enhanced Map Modal Styles */
.map-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow: hidden;
  transition: all 0.5s cubic-bezier(0.19, 1, 0.22, 1);
}

.map-modal-overlay.modal-active {
  background-color: rgba(0, 0, 0, 0.7);
}

/* Decorative background elements */
.modal-bg-elements {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
}

.geo-shape {
  position: absolute;
  border: 1px solid rgba(143, 0, 0, 0.2);
  opacity: 0.15;
  transform-origin: center;
}

.geo-dots {
  width: 200px;
  height: 200px;
  bottom: 20%;
  left: 20%;
  background-image: radial-gradient(var(--color-secondary) 1px, transparent 2px);
  background-size: 15px 15px;
  animation: rotateSlow 40s linear infinite;
}

.map-modal-container {
  background: linear-gradient(135deg, 
    rgba(42, 43, 56, 0.85),
    rgba(29, 30, 38, 0.95)
  );
  border-radius: var(--radius-box);
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.05),
    0 0 20px rgba(143, 0, 0, 0.2);
  position: relative;
  opacity: 0;
  transform: scale(0.9);
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: transform, opacity;
  perspective: 1000px;
  transform-style: preserve-3d;
}

.map-modal-container:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05),
    transparent 40%,
    transparent 60%,
    rgba(255, 255, 255, 0.05)
  );
  pointer-events: none;
  z-index: 1;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.map-modal-container:hover:before {
  opacity: 1;
}

.map-modal-container.in-view {
  opacity: 1;
  transform: scale(1);
}

/* Corner accents */
.corner-accent {
  position: absolute;
  width: 30px;
  height: 30px;
  z-index: 2;
  transition: transform 0.3s ease;
}

.corner-accent.top-left {
  top: 0;
  left: 0;
  border-top: 2px solid var(--color-primary);
  border-left: 2px solid var(--color-primary);
}

.corner-accent.top-right {
  top: 0;
  right: 0;
  border-top: 2px solid var(--color-primary);
  border-right: 2px solid var(--color-primary);
}

.corner-accent.bottom-left {
  bottom: 0;
  left: 0;
  border-bottom: 2px solid var(--color-primary);
  border-left: 2px solid var(--color-primary);
}

.corner-accent.bottom-right {
  bottom: 0;
  right: 0;
  border-bottom: 2px solid var(--color-primary);
  border-right: 2px solid var(--color-primary);
}

.map-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;
  z-index: 3;
}

.map-modal-header:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"><path fill="rgba(255,255,255,0.03)" d="M0 0h10v10H0zm10 10h10v10H10z"/></svg>');
  opacity: 0.1;
  pointer-events: none;
}

.map-modal-title {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: white;
  z-index: 2;
}

.icon-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
}

.location-icon {
  font-size: 1.5rem;
  color: white;
  position: relative;
  z-index: 2;
}

.pulse-ring {
  position: absolute;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.7);
  animation: pulse-ring 2s infinite;
  opacity: 0.7;
}

.modal-close {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
}

.modal-close:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  transform: scale(0);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-close:hover:before {
  transform: scale(1);
}

.modal-close .hover {
  animation: wiggle 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.map-modal-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  padding: 1.5rem;
  overflow: auto;
  max-height: calc(90vh - 72px);
  position: relative;
  z-index: 2;
}

@media (min-width: 768px) {
  .map-modal-content {
    grid-template-columns: 3fr 1fr;
    gap: 1.5rem;
  }
}

.map-container {
  width: 100%;
  height: 450px;
  border-radius: var(--radius-box);
  overflow: hidden;
  position: relative;
  box-shadow: 
    0 10px 25px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.05),
    inset 0 0 10px rgba(0, 0, 0, 0.3);
  transform: translateY(30px);
  opacity: 0;
  animation: slide-up 0.5s 0.1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.map-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(143, 0, 0, 0.3) 0%,
    transparent
  );
  z-index: 1;
  pointer-events: none;
  opacity: 0.4;
  transition: opacity 0.3s ease;
  mix-blend-mode: color;
}

iframe {
  border: none;
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 0;
}

.loading-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(29, 30, 38, 0.8);
  z-index: 2;
}

.spinner {
  font-size: 2rem;
  color: white;
  animation: spin 1.5s linear infinite;
  margin-bottom: 1rem;
}

.map-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-card {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: var(--radius-box);
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  height: 100%;
  transform: translateY(30px);
  opacity: 0;
  animation: slide-up 0.5s 0.2s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.info-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
}

.info-card:hover .card-bg-effect {
  transform: translate(-50%, -50%) rotate(45deg) scale(1.5);
  opacity: 0.2;
}

.card-bg-effect {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, 
    rgba(143, 0, 0, 0.8) 0%, 
    rgba(143, 0, 0, 0.3) 50%,
    transparent 70%
  );
  transform: translate(-50%, -50%) rotate(0deg);
  opacity: 0.1;
  transition: all 0.8s ease;
  pointer-events: none;
  z-index: 0;
}

.info-content {
  position: relative;
  z-index: 1;
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.info-card h4 {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: var(--color-secondary);
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
}

.info-card h4:after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -1px;
  width: 50px;
  height: 2px;
  background: var(--color-primary);
}

.info-details {
  flex: 1;
}

.info-card p {
  margin-bottom: 0.5rem;
  color: var(--color-secondary);
  position: relative;
  z-index: 1;
}

.directions-link {
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px dashed rgba(255, 255, 255, 0.1);
}

.cta-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: var(--radius-field);
  background: transparent;
  color: var(--color-secondary);
  font-weight: bold;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.cta-text, .cta-icon {
  position: relative;
  z-index: 2;
  transition: transform 0.3s ease, color 0.3s ease;
}

.cta-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--color-primary);
  transform: translateX(-100%);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 1;
}

.cta-button:hover .cta-background {
  transform: translateX(0);
}

.cta-button:hover {
  color: white;
  border-color: var(--color-primary);
}

.cta-button:hover .cta-icon {
  transform: translateX(5px);
}

.info-highlight {
  background: rgba(29, 30, 38, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: var(--radius-box);
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  transform: translateY(30px);
  opacity: 0;
  animation: slide-up 0.5s 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.highlight-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.highlight-icon {
  font-size: 1.5rem;
  color: var(--color-primary);
  background: rgba(143, 0, 0, 0.1);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  padding: 0.5rem;
}

.highlight-label {
  font-size: 0.9rem;
  color: var(--color-secondary);
  opacity: 0.7;
  margin-bottom: 0.25rem;
}

.highlight-value {
  color: var(--color-secondary);
  font-weight: bold;
}

.highlight-value a {
  color: var(--color-secondary);
  transition: color 0.3s ease;
}

.highlight-value a:hover {
  color: var(--color-primary);
}

/* Animation keyframes */
@keyframes pulse-ring {
  0% {
    transform: scale(0.8);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.4;
  }
  100% {
    transform: scale(0.8);
    opacity: 0.8;
  }
}

@keyframes wiggle {
  0%, 100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-12deg);
  }
  75% {
    transform: rotate(12deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes slide-up {
  0% {
    transform: translateY(30px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes rotateSlow {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(180deg);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1) rotate(45deg);
    opacity: 0.2;
  }
  50% {
    transform: scale(1.1) rotate(45deg);
    opacity: 0.3;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-30px) rotate(10deg);
  }
}

@keyframes slide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Modal transition animations */
.modal-fade-enter-active {
  transition: opacity 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.34, 0.64, 0.84, 1);
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* Don't remove these existing animations */
@keyframes bounce {
   0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

/* Responsive design */
@media (max-width: 992px) {
  .contact-info {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .contact-wrapper {
    padding: 2rem 1rem;
  }
  
  .contact-info {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  h3 {
    font-size: 1.1rem;
  }
  
  .social-links {
    justify-content: center;
  }
  
  .newsletter {
    text-align: left;
  }
  
  .back-to-top {
    padding: 12px 0;
  }
  
  .btn-content {
    font-size: 0.9rem;
  }
  
  .copyright {
    margin-top: 2rem;
  }
  
  .map-modal-container {
    width: 95%;
  }
  
  .map-container {
    height: 300px;
  }
  
  .map-modal-title h3 {
    font-size: 1rem;
  }
  
  .corner-accent {
    width: 20px;
    height: 20px;
  }
  
  .info-card {
    height: auto;
  }
}
</style>
